import { Badge } from '@/components/ui/badge';
import { statusToneClasses } from '@/components/ui/status-badge';
import { cn } from '@/lib/utils';
import {
  EMPLOYEE_DATA_ADDRESS_KEYS,
  EMPLOYEE_DATA_CONTACT_KEYS,
  EMPLOYEE_DATA_PHONE_KEYS,
  getEmployeeDataContactOpLabel,
  getEmployeeDataFieldLabel,
  getEmployeeDataSectionLabel,
  parseEmployeeDataBody,
  type EmployeeDataBody,
  type EmployeeDataContactChange,
  type EmployeeDataContactOp,
  type EmployeeDataField,
  type I18nLike,
} from '@/types/changeRequest';
import { GenericPayload } from './GenericPayload';

interface EmployeeDataDiffProps {
  /**
   * The `employee_data` executor body. Typed as the pinned shape, but it
   * arrives as an opaque payload, so it is re-checked here: a body that fails
   * the guard renders the raw payload instead of an empty block.
   */
  body: EmployeeDataBody | Record<string, unknown> | undefined;
  i18n: I18nLike;
}

// Placeholder for a value that is absent on one side (added / removed key).
const EMPTY_VALUE = '-';

// Sort fields into the fixed display order of their key set.
function inKeyOrder(fields: EmployeeDataField[], order: readonly string[]): EmployeeDataField[] {
  return [...fields].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
}

const OP_BADGE_CLASS: Record<EmployeeDataContactOp, string> = {
  add: statusToneClasses.success,
  edit: statusToneClasses.warning,
  remove: statusToneClasses.danger,
};

function FieldDiffList({ fields, i18n }: { fields: EmployeeDataField[]; i18n: I18nLike }) {
  const previousLabel = i18n._({ id: 'changeRequest.employeeData.previousValue', message: 'previous value' });
  const newLabel = i18n._({ id: 'changeRequest.employeeData.newValue', message: 'new value' });
  return (
    <ul className="space-y-1">
      {fields.map((field) => (
        <li
          key={field.key}
          data-testid={`employee-data-field-${field.key}`}
          className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-3 sm:gap-2"
        >
          <span className="font-medium text-muted-foreground">{getEmployeeDataFieldLabel(i18n, field.key)}</span>
          <del className="break-words text-muted-foreground line-through">
            <span className="sr-only">{previousLabel} </span>
            {field.old ?? EMPTY_VALUE}
          </del>
          <ins className="break-words font-medium no-underline">
            <span className="sr-only">{newLabel} </span>
            {field.new ?? EMPTY_VALUE}
          </ins>
        </li>
      ))}
    </ul>
  );
}

// The contact's display name: the requested name, else the name at submit.
function contactName(change: EmployeeDataContactChange): string {
  const name = change.fields.find((f) => f.key === 'name');
  return name?.new ?? name?.old ?? '';
}

function ContactDiff({ change, i18n }: { change: EmployeeDataContactChange; i18n: I18nLike }) {
  const name =
    contactName(change) ||
    i18n._({ id: 'changeRequest.employeeData.contact', message: 'Emergency contact' });
  return (
    <li data-testid={`employee-data-contact-${change.op}`} className="space-y-2 rounded-md border p-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{name}</span>
        <Badge variant="outline" className={cn(OP_BADGE_CLASS[change.op])}>
          {getEmployeeDataContactOpLabel(i18n, change.op)}
        </Badge>
      </div>
      <FieldDiffList fields={inKeyOrder(change.fields, EMPLOYEE_DATA_CONTACT_KEYS)} i18n={i18n} />
    </li>
  );
}

/**
 * EmployeeDataDiff renders an `employee_data` change request (#1766) as a
 * before-and-after diff: one row per changed field, the old value struck
 * through and the new value beside it, each with screen-reader text. Emergency
 * contacts render grouped, each with an added / changed / removed badge.
 * Semantic list markup, no table; state is carried by text, not colour alone.
 */
export function EmployeeDataDiff({ body, i18n }: EmployeeDataDiffProps) {
  const parsed = parseEmployeeDataBody(body);
  if (!parsed) {
    return <GenericPayload payload={body} i18n={i18n} data-testid="employee-data-raw" />;
  }

  let content: React.ReactNode;
  switch (parsed.section) {
    case 'address':
      content = <FieldDiffList fields={inKeyOrder(parsed.fields, EMPLOYEE_DATA_ADDRESS_KEYS)} i18n={i18n} />;
      break;
    case 'phone':
      content = <FieldDiffList fields={inKeyOrder(parsed.fields, EMPLOYEE_DATA_PHONE_KEYS)} i18n={i18n} />;
      break;
    case 'emergency_contacts':
      content = (
        <ul className="space-y-2">
          {parsed.contacts.map((change, idx) => (
            <ContactDiff key={`${change.op}-${change.id ?? 'new'}-${idx}`} change={change} i18n={i18n} />
          ))}
        </ul>
      );
      break;
  }

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3" data-testid="employee-data-diff">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{parsed.employeeName}</span>
        <Badge variant="secondary">{getEmployeeDataSectionLabel(i18n, parsed.section)}</Badge>
      </div>
      {content}
    </div>
  );
}
